import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { capture, EVENTS } from "../analytics/posthog";
import { PublicNav } from "../components/public/PublicNav";
import { PublicFooter } from "../components/public/PublicFooter";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  ArrowRight, Search, Radar, ListFilter, Hammer, PlayCircle, ShieldCheck,
  UserX, BrainCircuit, Stamp, Workflow, Handshake, ArrowUpFromLine,
  Scale, Cog, BookLock, LineChart, Users, KeyRound, Check, MessageSquare, Sparkles,
} from "lucide-react";

const PROBLEMS = [
  { icon: Stamp, t: "Las decisiones se detienen", d: "Cuando no estás disponible, el equipo espera tu aprobación para avanzar." },
  { icon: BrainCircuit, t: "Conocimiento solo en una cabeza", d: "Información crítica que no está documentada y vive en una única persona." },
  { icon: Handshake, t: "Aprobación constante", d: "Los empleados necesitan tu visto bueno una y otra vez para operar." },
  { icon: Workflow, t: "Procesos con un único responsable", d: "Procesos clave que dependen de que una persona concreta esté presente." },
  { icon: ArrowUpFromLine, t: "Ventas atadas al fundador", d: "Clientes o ingresos que dependen directamente de ti para cerrarse." },
  { icon: UserX, t: "Todo escala de vuelta a ti", d: "Los problemas vuelven al propietario en lugar de resolverse en su nivel." },
];

const STEPS = [
  { icon: Search, t: "Diagnostica", d: "Entiende cómo funciona realmente tu empresa a través de una conversación guiada." },
  { icon: Radar, t: "Detecta", d: "Identifica las dependencias del propietario y de las personas clave." },
  { icon: ListFilter, t: "Prioriza", d: "Determina qué dependencia conviene eliminar primero por impacto y riesgo." },
  { icon: Hammer, t: "Construye", d: "Architect diseña la intervención o el sistema operativo adecuado." },
  { icon: PlayCircle, t: "Implementa", d: "La empresa lo usa en la operación real, no en teoría." },
  { icon: ShieldCheck, t: "Verifica", d: "La evidencia determina si la dependencia se ha reducido de verdad." },
];

const ARCHITECT_CAPS = [
  "Entiende el contexto de tu empresa",
  "Recuerda tu modelo operativo",
  "Identifica qué es lo más importante ahora",
  "Recomienda una única next best action",
  "Convierte problemas en sistemas operativos",
  "Guía la implementación en la operación real",
  "Interpreta la evidencia operativa",
  "Verifica si la dependencia ha disminuido",
];

const AREAS = [
  { icon: Scale, t: "Decisiones", d: "Quién puede decidir y hasta qué límite sin ti." },
  { icon: Cog, t: "Operaciones", d: "Procesos que funcionan sin un responsable único." },
  { icon: BookLock, t: "Conocimiento", d: "Información crítica documentada y accesible." },
  { icon: LineChart, t: "Continuidad comercial", d: "Ventas e ingresos que no dependen del fundador." },
  { icon: Users, t: "Personas", d: "Roles con backup y responsabilidades definidas." },
  { icon: KeyRound, t: "Control", d: "Supervisión sin necesidad de estar en todo." },
];

const TRUST = [
  "El contexto de tu empresa se mantiene organizado.",
  "Las recomendaciones se basan en tu modelo operativo real.",
  "El progreso se verifica con evidencia, no con casillas marcadas.",
  "Los cambios estructurales requieren tu confirmación explícita.",
];

const FAQ = [
  { q: "¿Qué es INDEPENDENT?", a: "Un software B2B que ayuda a propietarios de pymes a detectar dónde su empresa depende en exceso de ellos o de personas clave, y a construir los sistemas necesarios para reducir esa dependencia." },
  { q: "¿Para quién es?", a: "Para propietarios de pymes y empresas dirigidas por su fundador que quieren que el negocio pueda operar sin depender de una sola persona." },
  { q: "¿Esto es un chatbot?", a: "No. La IA es el motor, no el producto. Architect es la interfaz que entiende tu empresa, recomienda la siguiente acción y te ayuda a convertir problemas en sistemas operativos verificables." },
  { q: "¿Qué hace Architect?", a: "Entiende el contexto de tu empresa, recuerda tu modelo operativo, identifica qué importa a continuación, construye sistemas de delegación, guía la implementación e interpreta la evidencia para verificar si la dependencia ha bajado." },
  { q: "¿Para qué se usan los créditos?", a: "El chat, el diagnóstico, la navegación, la guía de implementación y la verificación son gratuitos. Los créditos solo se usan para el trabajo de alto valor de Architect: construir un sistema (15 créditos) o un rediseño sustancial (5 créditos)." },
  { q: "¿Necesito una suscripción?", a: "No para empezar. Recibes 25 créditos de bienvenida (una vez) y acceso a la plataforma completa sin tarjeta. Las suscripciones amplían tu capacidad mensual de trabajo de Architect." },
  { q: "¿Qué pasa con los créditos comprados?", a: "Los créditos de top-up que compras no caducan por renovación, cancelación ni cambio de plan. Los créditos incluidos en la suscripción se renuevan cada ciclo y no se acumulan." },
  { q: "¿Puedo cancelar mi suscripción?", a: "Sí. Puedes gestionar o cancelar tu suscripción cuando quieras desde el portal de facturación. Al cancelar, vuelves al plan gratuito y tus créditos comprados se conservan." },
  { q: "¿INDEPENDENT sustituye a consultores o directivos?", a: "No. INDEPENDENT estructura y verifica la reducción de dependencias de tu empresa; el criterio y las decisiones siguen siendo tuyos y de tu equipo." },
];

const FALLBACK_PLANS = [
  { id: "starter", name: "Starter", price: 19.99, credits: 30, features: ["Plataforma INDEPENDENT completa", "30 créditos/mes (≈ 2 Architect Builds)", "Diagnóstico, mapa, plan, chat y verificación sin coste de créditos"] },
  { id: "pro", name: "Pro", price: 39.99, credits: 75, features: ["Plataforma INDEPENDENT completa", "75 créditos/mes (≈ 5 Architect Builds)", "Más capacidad mensual para construir sistemas"] },
  { id: "scale", name: "Scale", price: 79.99, credits: 180, features: ["Plataforma INDEPENDENT completa", "180 créditos/mes (≈ 12 Architect Builds)", "Máxima capacidad mensual para construir sistemas"] },
];
const FALLBACK_TOPUPS = [
  { id: "15", credits: 15, price: 7.99 }, { id: "50", credits: 50, price: 19.99 }, { id: "150", credits: 150, price: 49.99 },
];

// Euro symbol in body font to avoid the Syne display-font glyph overlap.
const Eur = () => <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} className="font-normal">€</span>;

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [topups, setTopups] = useState(FALLBACK_TOPUPS);

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    capture(EVENTS.landingViewed, { landing_page: window.location.pathname });
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (data?.pricing?.plans?.length) setPlans(data.pricing.plans);
      if (data?.pricing?.topups?.length) setTopups(data.pricing.topups);
    }).catch(() => {});
  }, []);

  const startFree = (cta) => {
    if (!user) capture(EVENTS.signupClicked, { cta: typeof cta === "string" ? cta : "landing" });
    navigate(user ? (user.onboarding_complete ? "/app" : "/onboarding") : "/auth?mode=register");
  };
  const login = () => navigate("/auth");

  return (
    <div className="min-h-screen bg-obsidian text-slate-100">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden grid-bg pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div className="halo" style={{ right: "6%", top: "6%" }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="fade-up">
              <span className="mono-label text-amber-400">Business Independence OS</span>
              <h1 className="mt-5 font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Crea una empresa que funcione <span className="text-amber-400 text-glow-amber">sin depender de ti</span>.
              </h1>
              <ul className="mt-7 space-y-2.5 text-base sm:text-lg text-slate-400 max-w-xl">
                <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />Detecta dónde tu empresa depende de ti.</li>
                <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />Prioriza las dependencias que importan.</li>
                <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />Construye los sistemas para eliminarlas.</li>
                <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />Verifica que el negocio puede operar sin ti.</li>
              </ul>
              <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button data-testid="hero-start-free-button" onClick={() => startFree("hero")} size="lg"
                  className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold text-base px-7 h-12">
                  Empezar gratis <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button data-testid="hero-login-button" onClick={login} variant="outline" size="lg"
                  className="bg-white/[0.03] border-white/12 text-white hover:bg-white/[0.07] rounded-full font-semibold text-base px-7 h-12">
                  Entrar
                </Button>
              </div>
              <p className="mt-5 mono-label text-slate-500">25 créditos de bienvenida · Sin tarjeta</p>
            </div>

            {/* Truthful Architect console mock */}
            <div className="fade-up card-pub p-6 sm:p-7" style={{ animationDelay: ".12s" }} data-testid="hero-product-visual">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 mono-label text-amber-400"><Sparkles className="h-3.5 w-3.5" /> ARCHITECT</div>
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-0.5 font-mono text-[0.65rem] text-slate-400">Ejemplo ilustrativo</span>
              </div>
              <div className="mt-5 space-y-3">
                <div className="flex justify-end">
                  <p className="max-w-[80%] rounded-2xl rounded-br-sm bg-white/[0.05] px-4 py-2.5 text-sm text-slate-200">
                    Cuando no estoy, las cotizaciones grandes se paran.
                  </p>
                </div>
                <div className="flex gap-2.5">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-500/15"><MessageSquare className="h-3.5 w-3.5 text-amber-400" /></div>
                  <p className="max-w-[85%] rounded-2xl rounded-bl-sm bg-amber-500/[0.06] border border-amber-500/15 px-4 py-2.5 text-sm text-slate-200">
                    Eso es una dependencia del propietario en ventas. Puedo construir un sistema de decisión que permita al equipo cotizar hasta un límite sin ti.
                  </p>
                </div>
              </div>
              <div className="mt-5 rounded-xl border border-white/10 bg-[#0B0D13] p-4">
                <p className="mono-label text-slate-500">Next best action</p>
                <p className="mt-1.5 text-sm font-semibold text-white">Construir: Protocolo de cotizaciones grandes sin el fundador</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Reduce dependencia en ventas</span>
                  <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 font-mono text-[0.65rem] text-amber-300">Architect Build · 15 créditos</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section id="producto" className="scroll-mt-20 border-y border-white/10 bg-[#0B0D13] py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <span className="mono-label text-amber-400">El problema</span>
          <h2 className="mt-3 font-head text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-3xl">
            Una empresa puede ser rentable y aun así depender peligrosamente de su propietario.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl">Estas son las señales de una empresa que no puede operar sin una persona concreta:</p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROBLEMS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.t} className="card-pub p-6" data-testid={`problem-${p.t}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                    <Icon className="h-5 w-5 text-amber-400" />
                  </div>
                  <p className="mt-4 font-head text-lg font-semibold text-white">{p.t}</p>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="scroll-mt-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <span className="mono-label text-amber-400">Cómo funciona</span>
        <h2 className="mt-3 font-head text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Un ciclo operativo, guiado por estado y evidencia</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">El progreso no se mide en semanas: avanza según el estado real de cada sistema y la evidencia que lo respalda.</p>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.t} className="card-pub p-6" data-testid={`step-${s.t}`}>
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                    <Icon className="h-5 w-5 text-amber-400" />
                  </div>
                  <span className="font-mono text-sm text-slate-600">0{i + 1}</span>
                </div>
                <p className="mt-4 font-head text-lg font-bold text-white">{s.t}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Architect */}
      <section className="border-y border-white/10 bg-[#0B0D13] py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="mono-label text-amber-400">Architect</span>
            <h2 className="mt-3 font-head text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">La interfaz central de tu empresa</h2>
            <p className="mt-4 text-slate-400 max-w-xl">
              Architect no es un chatbot genérico. Conoce tu modelo operativo y te lleva de un problema
              real a un sistema operativo verificable.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {ARCHITECT_CAPS.map((c) => (
                <div key={c} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />{c}
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4">
              <p className="text-sm text-slate-300">
                <b className="text-white">Chat, diagnóstico, navegación, guía de implementación y verificación son gratuitos.</b>{" "}
                Los créditos se reservan para el trabajo de alto valor de Architect.
              </p>
            </div>
          </div>

          {/* Truthful verification / before-after mock */}
          <div className="card-pub p-6 sm:p-7" data-testid="architect-verify-visual">
            <div className="flex items-center justify-between">
              <p className="mono-label text-amber-400">Verificación por evidencia</p>
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-0.5 font-mono text-[0.65rem] text-slate-400">Ejemplo ilustrativo</span>
            </div>
            <div className="mt-5 flex items-end gap-5">
              <div>
                <p className="mono-label text-slate-500">Antes</p>
                <p className="font-head text-4xl font-extrabold text-slate-300">22</p>
              </div>
              <ArrowRight className="mb-2 h-5 w-5 text-amber-400" />
              <div>
                <p className="mono-label text-slate-500">Después</p>
                <p className="font-head text-4xl font-extrabold text-amber-400 text-glow-amber">25</p>
              </div>
              <span className="mb-2 ml-auto rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[0.65rem] text-emerald-400">Verificado</span>
            </div>
            <div className="mt-6 space-y-3">
              {[["Dependencia del propietario", "64", "58", "text-emerald-400"], ["Escalados al fundador", "8", "1", "text-emerald-400"]].map(([l, a, b]) => (
                <div key={l} className="flex items-center justify-between rounded-lg border border-white/8 bg-[#0B0D13] px-4 py-3">
                  <span className="text-sm text-slate-300">{l}</span>
                  <span className="font-mono text-sm text-slate-400">{a} <span className="text-slate-600">→</span> <span className="text-emerald-400">{b}</span></span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-slate-500">La mejora se atribuye solo cuando la evidencia confirma que la dependencia ha bajado.</p>
          </div>
        </div>
      </section>

      {/* Method / areas */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <span className="mono-label text-amber-400">El método</span>
        <h2 className="mt-3 font-head text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Evaluamos la independencia de tu empresa por áreas</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">No te mostramos fórmulas: te mostramos dónde se concentra la dependencia y qué cambia cuando la reduces.</p>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {AREAS.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.t} className="card-pub p-6" data-testid={`area-${a.t}`}>
                <Icon className="h-5 w-5 text-amber-400" />
                <p className="mt-4 font-head text-lg font-semibold text-white">{a.t}</p>
                <p className="mt-2 text-sm text-slate-400">{a.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="precios" className="border-y border-white/10 bg-[#0B0D13] py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <span className="mono-label text-amber-400">Precios</span>
          <h2 className="mt-3 font-head text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Empieza gratis. Paga por capacidad de trabajo de Architect.</h2>
          <p className="mt-4 text-slate-400 max-w-2xl">Todos los planes acceden a la misma plataforma completa. La diferencia es la capacidad mensual de trabajo de Architect.</p>

          <div className="mt-12 grid lg:grid-cols-4 md:grid-cols-2 gap-5">
            {/* Free */}
            <div className="card-pub p-6" data-testid="pricing-free">
              <div className="h-5 mb-1" />
              <h3 className="font-head text-xl font-bold text-white">Free</h3>
              <p className="mt-3"><span className="font-head text-4xl font-extrabold text-white">0</span><span className="font-head text-4xl font-extrabold text-white"><Eur /></span></p>
              <p className="mt-1 text-sm text-amber-400 font-mono">25 créditos de bienvenida (una vez)</p>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
                <li className="flex gap-2"><Check className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />Plataforma INDEPENDENT completa</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />Diagnóstico, mapa, plan, chat y verificación sin coste de créditos</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />Empieza sin suscripción ni tarjeta</li>
              </ul>
              <Button data-testid="pricing-cta-free" onClick={() => startFree("pricing_free")} className="mt-6 w-full rounded-full font-semibold bg-amber-500 text-black hover:bg-amber-400">
                Empezar gratis
              </Button>
            </div>

            {/* Paid plans */}
            {plans.map((p) => {
              const popular = p.id === "pro";
              return (
                <div key={p.id} className={`card-pub p-6 ${popular ? "border-amber-500/40" : ""}`} data-testid={`pricing-${p.id}`}>
                  <div className="h-5 mb-1">{popular && <span className="mono-label text-amber-400">Más popular</span>}</div>
                  <h3 className="font-head text-xl font-bold text-white">{p.name}</h3>
                  <p className="mt-3"><span className="font-head text-3xl font-extrabold text-white">{p.price}<Eur /></span><span className="text-sm text-slate-500">/mes</span></p>
                  <p className="mt-1 text-sm text-amber-400 font-mono">{p.credits} créditos/mes</p>
                  <p className="text-xs text-slate-500">≈ hasta {Math.floor(p.credits / 15)} Architect Builds/mes</p>
                  <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
                    {p.features?.map((f) => (
                      <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />{f}</li>
                    ))}
                  </ul>
                  <Button data-testid={`pricing-cta-${p.id}`} onClick={() => startFree(`pricing_${p.id}`)}
                    className={`mt-6 w-full rounded-full font-semibold ${popular ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-white/[0.06] text-white hover:bg-white/[0.1]"}`}>
                    Empezar gratis
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Top-ups + paid ops */}
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <div className="card-pub p-6" data-testid="pricing-topups">
              <p className="mono-label text-slate-500">Top-ups de créditos</p>
              <p className="mt-2 text-sm text-slate-400">Créditos adicionales cuando los necesites. No caducan por renovación, cancelación ni cambio de plan.</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {topups.map((t) => (
                  <div key={t.id} className="rounded-xl border border-white/8 bg-[#0B0D13] p-4 text-center">
                    <p className="font-head text-2xl font-extrabold text-amber-400">{t.credits}</p>
                    <p className="text-xs text-slate-500">créditos</p>
                    <p className="mt-1 text-sm text-white font-mono">{t.price}€</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-pub p-6" data-testid="pricing-paid-ops">
              <p className="mono-label text-slate-500">Operaciones de pago</p>
              <p className="mt-2 text-sm text-slate-400">Solo el trabajo de alto valor de Architect consume créditos:</p>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0B0D13] px-4 py-3">
                  <span className="text-sm text-slate-200">Architect Build</span>
                  <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 font-mono text-xs text-amber-300">15 créditos</span>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0B0D13] px-4 py-3">
                  <span className="text-sm text-slate-200">Rediseño sustancial de sistema</span>
                  <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 font-mono text-xs text-amber-300">5 créditos</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500">Los créditos incluidos en la suscripción se renuevan cada ciclo y no se acumulan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <span className="mono-label text-amber-400">Transparencia</span>
        <h2 className="mt-3 font-head text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Metodología, no promesas vacías</h2>
        <p className="mt-4 text-slate-400 max-w-2xl">Sin testimonios inventados, sin logos falsos, sin ROI ficticio. Lo que ves es cómo funciona el producto.</p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {TRUST.map((t) => (
            <div key={t} className="card-pub p-6 flex items-start gap-3" data-testid={`trust-${t.slice(0, 12)}`}>
              <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-200">{t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/10 bg-[#0B0D13] py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center">Preguntas frecuentes</h2>
          <Accordion type="single" collapsible className="mt-10">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-white hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="card-pub p-10 sm:p-14 text-center">
          <div className="halo" style={{ left: "30%", top: "-30%" }} />
          <h2 className="relative font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Empieza por saber dónde depende tu empresa de ti</h2>
          <p className="relative mt-4 text-slate-400">25 créditos de bienvenida · Sin tarjeta</p>
          <div className="relative mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button data-testid="final-start-free-button" onClick={() => startFree("final")} size="lg"
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold text-base px-8 h-12">
              Empezar gratis <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button data-testid="final-login-button" onClick={login} variant="outline" size="lg"
              className="bg-white/[0.03] border-white/12 text-white hover:bg-white/[0.07] rounded-full font-semibold text-base px-8 h-12">
              Entrar
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
